import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { DateTime } from 'luxon';
import { useLocation } from 'react-router';
import {
  Check,
  ChevronDown,
  ChevronUp,
  Link as LinkIcon,
} from '@saleshandy/icons';
import { useSelector } from 'react-redux';
import {
  accessibleOnClick,
  executeOnRequestStatus,
  formatNoteTimestamp,
  formateTime,
} from '../../../../../../../../shared/utils';
import { RootState } from '../../../../../../../../store/root-reducer';
import { RequestStatus } from '../../../../../../../../shared/enums/request-status';
import {
  CallsResponse,
  prospectCallLogsDrawerHeaderPayload,
} from '../../../../../../../dialer/types';
import type { DialerPhoneNumberOption } from '../../../../../../../../shared/components/dialer/types';
import {
  CallDirection,
  CallLog,
  CallOutcomes,
  CallSource,
} from '../../../../../../../dialer/types/call-logs';
import { OutgoingCallIcon } from '../../../../../../../../shared/svg/outgoing-call';
import { IncomingCallIcon } from '../../../../../../../../shared/svg/incoming-call';
import { MissedCallIcon } from '../../../../../../../../shared/svg/missed-call';
import { getCallOutcomeIcon } from '../../../../../../../settings/components/call-outcomes/components/call-outcome-content/helpers/call-outcome-column-data';
import TruncatedTextWithAutoTooltip from '../../../../../../../../shared/components/truncated-text-with-auto-tooltip';
import CallLogsSingleCallDetail from '../../../../../../../dialer/components/call-logs/components/call-logs-missed-call-detail/call-logs-single-call-detail-container';
import { OutcomeSentiment } from '../../../../../../../settings/enums/custom-outcome';
import DateFilter from '../../../../../../../../shared/components/date-filter';
import { getDateRange } from '../../../../../../../../shared/components/date-filter/date-filter-options';
import { generateProspectDetailsUrl } from '../../../../../../../../shared/utils/prospect-details-url';
import CallsListTabSkeleton from './calls-list-tab-skeleton';
import { OverlayTooltip } from '../../../../../../../../shared/design-system/components/overlay';

interface CallsListTabDateFilterState {
  label: string;
  startDate: DateTime | null;
  endDate: DateTime | null;
  displayText: string;
}

const SELECT_RANGE_LABEL = 'select_range';

export type CallsListTabProps = {
  selectedProspectId: number;
  activeTab: string;
  onCallsCountChange?: (count: number) => void;
  getCallLogsProspectDrawerHeaderResponse?: CallsResponse;
  sendGetCallLogsProspectDrawerHeaderRequest?: (
    payload: prospectCallLogsDrawerHeaderPayload,
  ) => void;
  userTimeZone?: string;
  getCallLogsProspectDrawerHeaderRequestStatus?: RequestStatus;
  resetGetCallLogsProspectDrawerHeader?: () => void;
};

const CALLS_PAGE_SIZE = 25;

function formatCallDuration(seconds: number): string {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  const parts: string[] = [];
  if (hrs > 0) parts.push(`${hrs}hr`);
  if (mins > 0) parts.push(`${mins}m`);
  if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);
  return parts.join(' ');
}

type ProspectDrawerApiCall = CallsResponse['calls'][number];

const EMPTY_CALLOUTCOME: CallOutcomes = {
  id: 0,
  name: '',
  bgColor: '',
  textColor: '',
  sentiment: '',
  isDefault: true,
};

/**
 * Prospect calls list returns one row per call; map it to `CallLog` so `row.callId`,
 * transcription, and signed-url Redux keys match this list item (not global `singleCallDetails`).
 */
function prospectDrawerApiCallToCallLog(
  apiCall: ProspectDrawerApiCall,
): CallLog {
  const direction =
    apiCall.type === 'outgoing'
      ? CallDirection.Outgoing
      : CallDirection.Incoming;

  const prospectPhone: DialerPhoneNumberOption = {
    number: apiCall.prospectPhoneNumber,
  };

  return {
    id: apiCall.id,
    callId: String(apiCall.id),
    prospectName: '',
    prospectPhone,
    callDirection: direction,
    recordingDuration: apiCall.duration ?? 0,
    callDuration: apiCall.duration ?? 0,
    ringDuration: 0,
    callOutcome: apiCall.callOutcome
      ? (apiCall.callOutcome as CallOutcomes)
      : EMPTY_CALLOUTCOME,
    dateTime: apiCall.callStartedAt,
    callerHost: '',
    source: CallSource.ManualCall,
    transcription: apiCall.transcription ?? null,
    isTranscriptionInProgress: Boolean(apiCall.isTranscriptionInProgress),
    isRecordingPresent: Boolean(apiCall.isRecordingPresent),
    recordingUrl: null,
    aiSummary: apiCall.aiSummary ?? null,
    isAiSummaryInProgress: false,
  };
}

const CallsListTab: React.FC<CallsListTabProps> = memo(
  ({
    selectedProspectId,
    activeTab,
    onCallsCountChange,
    getCallLogsProspectDrawerHeaderResponse,
    sendGetCallLogsProspectDrawerHeaderRequest,
    // userTimeZone,
    getCallLogsProspectDrawerHeaderRequestStatus,
    resetGetCallLogsProspectDrawerHeader,
  }) => {
    const location = useLocation();
    const userTimezone = useSelector(
      (state: RootState) => state?.home?.timeZone,
    );
    const profileTimezone = useSelector(
      (state: RootState) => state?.myProfile?.myProfile?.timeZone,
    );

    const initialSelectedIdsRef = useRef<number[] | undefined>(
      (() => {
        try {
          const params = new URLSearchParams(location.search);
          const callIdParam = params.get('callId');
          if (!callIdParam) return undefined;
          const ids = callIdParam
            .split(',')
            .map((v) => Number(v.trim()))
            .filter((n) => Number.isFinite(n) && n > 0);
          return ids.length > 0 ? ids : undefined;
        } catch {
          return undefined;
        }
      })(),
    );
    const [accumulatedCalls, setAccumulatedCalls] = useState<
      CallsResponse['calls']
    >([]);
    const [callsTotal, setCallsTotal] = useState(0);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [expandedCallIds, setExpandedCallIds] = useState<string[]>([]);
    const [copiedCallLinkId, setCopiedCallLinkId] = useState<string | null>(
      null,
    );

    /** Same as tasks-list-tab / task-list: ref on the scrollable root (overflow-y on .bs-calls-list-tab). */
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const isLoadingMoreRef = useRef(false);
    const currentPageRef = useRef(1);
    const pendingAppendRef = useRef(false);
    const replaceEpochRef = useRef(0);
    const appendDispatchEpochRef = useRef<number | null>(null);

    const isRequestPending =
      getCallLogsProspectDrawerHeaderRequestStatus === RequestStatus.Pending;

    const [date, setDate] = useState<CallsListTabDateFilterState>(() => ({
      label: SELECT_RANGE_LABEL,
      startDate: null,
      endDate: null,
      displayText: 'Select Date Range',
    }));

    const buildDatePayload = useCallback(
      (label: string, start: DateTime | null, end: DateTime | null) => {
        if (label === SELECT_RANGE_LABEL || !start || !end) {
          return {};
        }
        const tz = profileTimezone || DateTime.local().zoneName;
        let effectiveStart = start;
        let effectiveEnd = end;

        // Mirror analytics tab: recompute presets fresh in profile tz so
        // stale module-level durationOptions can't drift the day.
        if (label !== 'custom') {
          const fresh = getDateRange(label, tz);
          if (fresh?.startDate && fresh?.endDate) {
            effectiveStart = fresh.startDate;
            effectiveEnd = fresh.endDate;
          }
        }

        // Mirror analytics tab: both dates pinned to startOf('day') in profile tz.
        effectiveStart = effectiveStart.setZone(tz).startOf('day');
        effectiveEnd = effectiveEnd.setZone(tz).startOf('day');

        return {
          startDate: effectiveStart.toISO() ?? '',
          endDate: effectiveEnd.toISO() ?? '',
        };
      },
      [profileTimezone],
    );

    const fetchFirstPage = useCallback(() => {
      replaceEpochRef.current += 1;
      pendingAppendRef.current = false;
      appendDispatchEpochRef.current = null;
      currentPageRef.current = 1;
      isLoadingMoreRef.current = false;
      setIsLoadingMore(false);
      setAccumulatedCalls([]);
      setCallsTotal(0);
    }, []);

    const onDateTimeSelectionChange = useCallback(
      (label: string, start: DateTime, end: DateTime, displayText: string) => {
        setDate({
          label,
          startDate: start,
          endDate: end,
          displayText,
        });
        fetchFirstPage();
        /* Page-1 fetch runs in useEffect when date state updates (avoids double request). */
      },
      [fetchFirstPage],
    );

    const toggleExpanded = useCallback((callId: string) => {
      setExpandedCallIds((prev) =>
        prev.includes(callId)
          ? prev.filter((id) => id !== callId)
          : [...prev, callId],
      );
    }, []);

    const formatRowTime = useCallback(
      (iso?: string) => {
        if (!iso) return '—';
        return formatNoteTimestamp(iso, false, userTimezone, false, true);
      },
      [userTimezone],
    );

    const handleCopyCallLink = useCallback(
      async (e: React.MouseEvent | React.KeyboardEvent, callRowId: string) => {
        e.preventDefault();
        e.stopPropagation();

        const callLogNumericId = Number(callRowId);
        if (!Number.isFinite(callLogNumericId) || callLogNumericId <= 0) {
          return;
        }

        const origin = window?.location?.origin ?? '';
        const path = generateProspectDetailsUrl(
          location.pathname,
          callLogNumericId,
          'calls',
        );

        const callLink = `${origin}/dialer/call-logs/${callRowId}/calls?callId=${encodeURIComponent(
          callRowId,
        )}`;

        if (
          typeof navigator === 'undefined' ||
          !navigator?.clipboard?.writeText
        ) {
          return;
        }

        try {
          await navigator.clipboard.writeText(callLink);
          setCopiedCallLinkId(callRowId);
          setTimeout(() => {
            setCopiedCallLinkId((prev) => (prev === callRowId ? null : prev));
          }, 2000);
        } catch {
          // Clipboard may be denied; avoid console noise in production UI
        }
      },
      [location.pathname],
    );

    const getIcon = (direction: CallDirection) => {
      switch (direction) {
        case CallDirection.Outgoing:
          return (
            <OutgoingCallIcon
              width={20}
              height={20}
              className="prospect-name-cell__left__icon"
            />
          );
        case CallDirection.Incoming:
          return (
            <IncomingCallIcon
              width={20}
              height={20}
              className="prospect-name-cell__left__icon"
            />
          );
        case CallDirection.Missed:
        case CallDirection.MissedWithVoicemail:
          return (
            <MissedCallIcon
              width={20}
              height={20}
              className="prospect-name-cell__left__icon prospect-name-cell__left__icon--missed"
            />
          );
        default:
          return null;
      }
    };

    useEffect(() => {
      if (!selectedProspectId) return;
      fetchFirstPage();
      const selectedIds = initialSelectedIdsRef.current;
      initialSelectedIdsRef.current = undefined;
      sendGetCallLogsProspectDrawerHeaderRequest({
        pageNumber: 1,
        pageSize: CALLS_PAGE_SIZE,
        prospectId: selectedProspectId,
        ...buildDatePayload(date.label, date.startDate, date.endDate),
        ...(selectedIds && selectedIds.length > 0 ? { selectedIds } : {}),
      });
    }, [
      selectedProspectId,
      date.label,
      date.startDate?.toISO(),
      date.endDate?.toISO(),
      sendGetCallLogsProspectDrawerHeaderRequest,
      fetchFirstPage,
      buildDatePayload,
    ]);

    useEffect(() => {
      if (
        getCallLogsProspectDrawerHeaderRequestStatus !== RequestStatus.Succeeded
      ) {
        return;
      }
      const res = getCallLogsProspectDrawerHeaderResponse;
      if (!res) return;

      const total = Number(res.total) || 0;
      setCallsTotal(total);
      onCallsCountChange?.(total);

      if (pendingAppendRef.current) {
        if (appendDispatchEpochRef.current !== replaceEpochRef.current) {
          pendingAppendRef.current = false;
          isLoadingMoreRef.current = false;
          setIsLoadingMore(false);
          return;
        }
        pendingAppendRef.current = false;
        appendDispatchEpochRef.current = null;
        isLoadingMoreRef.current = false;
        setIsLoadingMore(false);
        currentPageRef.current += 1;
        setAccumulatedCalls((prev) => {
          const seen = new Set(prev.map((c) => c.id));
          const next = [...prev];
          res.calls.forEach((c) => {
            if (!seen.has(c.id)) {
              seen.add(c.id);
              next.push(c);
            }
          });
          return next;
        });
        return;
      }

      currentPageRef.current = 1;
      setAccumulatedCalls(res.calls);
    }, [
      getCallLogsProspectDrawerHeaderResponse,
      getCallLogsProspectDrawerHeaderRequestStatus,
    ]);

    useEffect(() => {
      setExpandedCallIds([]);
      setCopiedCallLinkId(null);
    }, [
      selectedProspectId,
      date.label,
      date.startDate?.toISO(),
      date.endDate?.toISO(),
    ]);

    const hasMore =
      accumulatedCalls.length > 0 && accumulatedCalls.length < callsTotal;

    const loadMore = useCallback(() => {
      if (!hasMore || isLoadingMore || isRequestPending) return;
      const nextPage = currentPageRef.current + 1;
      appendDispatchEpochRef.current = replaceEpochRef.current;
      pendingAppendRef.current = true;
      isLoadingMoreRef.current = true;
      setIsLoadingMore(true);
      sendGetCallLogsProspectDrawerHeaderRequest({
        pageNumber: nextPage,
        pageSize: CALLS_PAGE_SIZE,
        prospectId: selectedProspectId,
        ...buildDatePayload(date.label, date.startDate, date.endDate),
      });
    }, [
      hasMore,
      isLoadingMore,
      isRequestPending,
      selectedProspectId,
      date.label,
      date.startDate,
      date.endDate,
      sendGetCallLogsProspectDrawerHeaderRequest,
      buildDatePayload,
    ]);

    useEffect(() => {
      isLoadingMoreRef.current = isLoadingMore;
    }, [isLoadingMore]);

    /* Same infinite-scroll pattern as task-list.tsx: scroll listener on scrollContainerRef, 100px from bottom. */
    const handleScroll = useCallback(() => {
      const container = scrollContainerRef?.current;
      if (!container) return;
      if (activeTab !== 'calls') return;
      if (!accumulatedCalls.length) return;
      if (isLoadingMoreRef.current) return;

      const { scrollTop, scrollHeight, clientHeight } = container;
      if (scrollHeight - scrollTop <= clientHeight + 100) {
        if (hasMore && !isLoadingMore && !isRequestPending) {
          loadMore();
        }
      }
    }, [
      activeTab,
      accumulatedCalls.length,
      hasMore,
      isLoadingMore,
      isRequestPending,
      loadMore,
      scrollContainerRef,
    ]);

    useEffect(() => {
      const container = scrollContainerRef?.current;
      if (!container) return undefined;
      container.addEventListener('scroll', handleScroll, { passive: true });
      return () => {
        container.removeEventListener('scroll', handleScroll);
      };
    }, [handleScroll, scrollContainerRef]);

    useEffect(() => {
      if (
        getCallLogsProspectDrawerHeaderRequestStatus === RequestStatus.Failed
      ) {
        pendingAppendRef.current = false;
        isLoadingMoreRef.current = false;
        setIsLoadingMore(false);
      }
    }, [getCallLogsProspectDrawerHeaderRequestStatus]);

    useEffect(() => {
      executeOnRequestStatus({
        status: getCallLogsProspectDrawerHeaderRequestStatus,
        onSuccess: () => {
          resetGetCallLogsProspectDrawerHeader?.();
        },
        onFailed: () => {
          resetGetCallLogsProspectDrawerHeader?.();
        },
      });
    }, [getCallLogsProspectDrawerHeaderRequestStatus]);

    if (!selectedProspectId) {
      return (
        <div className="bs-calls-list-tab regular-2 gray-txt-11">
          Select a prospect to see calls.
        </div>
      );
    }

    return (
      <div className="bs-calls-list-tab" ref={scrollContainerRef}>
        {!isRequestPending && (
          <div className="bs-calls-list-tab__date-filter">
            <DateFilter
              selectedDateRangeOption={date.label}
              startDate={date.startDate}
              endDate={date.endDate}
              displayText={date.displayText}
              onDateTimeSelectionChange={onDateTimeSelectionChange}
              shouldHaveMinMaxValueLimit={false}
              shouldShowDateTooltip={true}
              shouldHaveMaxValueLimit={true}
            />
          </div>
        )}
        {accumulatedCalls.length === 0 && isRequestPending && (
          <CallsListTabSkeleton />
        )}
        {!isRequestPending &&
          accumulatedCalls.length === 0 &&
          callsTotal === 0 && (
            <div className="bs-calls-list-tab__empty regular-2 gray-txt-11">
              No calls yet for this prospect.
            </div>
          )}
        {accumulatedCalls.length > 0 && (
          <div className="bs-calls-list-tab__list-scroll">
            <ul className="bs-calls-list-tab__list">
              {accumulatedCalls.map((call, index) => {
                const id =
                  call?.id != null ? String(call.id) : `call-row-${index}`;
                const open = expandedCallIds.includes(id);
                const durationSec = call.duration ?? 0;
                const durationLabel = formatCallDuration(durationSec);

                return (
                  <li key={id} className="bs-calls-list-tab__item">
                    <div
                      className="bs-calls-list-tab__row-main"
                      {...accessibleOnClick(() => toggleExpanded(id))}
                    >
                      <div className="bs-calls-list-tab__row-main__left">
                        <div>{getIcon(call?.type as CallDirection)}</div>
                        {call?.callStartedAt && (
                          <span className="bs-calls-list-tab__row-main__time">
                            {formatRowTime(call?.callStartedAt)}
                          </span>
                        )}
                        {durationSec > 0 && (
                          <div className="bs-calls-list-tab__row-main__duration">
                            {durationLabel}
                          </div>
                        )}
                        {call?.callOutcome && (
                          <div
                            className={`bs-call-outcome-cell-readonly ${
                              // eslint-disable-next-line no-nested-ternary
                              call?.callOutcome?.sentiment ===
                              OutcomeSentiment.Positive
                                ? 'outcome-positive-sentiment'
                                : // eslint-disable-next-line no-nested-ternary
                                call?.callOutcome?.sentiment ===
                                  OutcomeSentiment.Negative
                                ? 'outcome-negative-sentiment'
                                : ''
                            }`}
                          >
                            <span className="bs-call-outcome-cell-readonly__icon flex-shrink-0">
                              {getCallOutcomeIcon(
                                String(call.callOutcome?.name),
                                Boolean(call.callOutcome?.isDefault),
                                String(call?.callOutcome?.sentiment ?? ''),
                                { width: 14, height: 14 },
                              )}
                            </span>
                            <TruncatedTextWithAutoTooltip
                              text={call.callOutcome?.name}
                              className="bs-call-outcome-cell-readonly__label"
                            />
                          </div>
                        )}
                        <button
                          type="button"
                          className="bs-calls-list-tab__row-main__left__link"
                          {...accessibleOnClick((e) =>
                            handleCopyCallLink(e, id),
                          )}
                          aria-label={
                            copiedCallLinkId === id
                              ? 'Call link copied'
                              : 'Copy call link'
                          }
                        >
                          {copiedCallLinkId === id ? (
                            <OverlayTooltip text="Call Link Copied">
                              <span className="d-flex align-items-center">
                                <Check height={14} width={14} />
                              </span>
                            </OverlayTooltip>
                          ) : (
                            <OverlayTooltip text="Copy Call Link">
                              <span className="d-flex align-items-center">
                                <LinkIcon height={14} width={14} />
                              </span>
                            </OverlayTooltip>
                          )}
                        </button>
                      </div>
                      <div className="bs-calls-list-tab__row-main__right">
                        <span className="bs-calls-list-tab__row-main__right__time">
                          {formatNoteTimestamp(
                            call?.callStartedAt,
                            false,
                            userTimezone,
                            false,
                            false,
                            true,
                          )}
                        </span>
                        <button
                          type="button"
                          className="bs-calls-list-tab__row"
                          aria-expanded={open}
                        >
                          <span
                            className="bs-calls-list-tab__chevron"
                            aria-hidden
                          >
                            {open ? (
                              <ChevronUp width={16} height={16} />
                            ) : (
                              <ChevronDown width={16} height={16} />
                            )}
                          </span>
                        </button>
                      </div>
                    </div>
                    {open && (
                      <CallLogsSingleCallDetail
                        row={prospectDrawerApiCallToCallLog(call)}
                        callLogsProspectProfileSection={call}
                        callLogsProspectDrawerListing={true}
                      />
                    )}
                  </li>
                );
              })}
            </ul>
            {isLoadingMore && (
              <div className="bs-calls-list-tab__list-load-more">
                <Spinner animation="border" size="sm" />
              </div>
            )}
          </div>
        )}
      </div>
    );
  },
);

CallsListTab.displayName = 'CallsListTab';

export default CallsListTab;
